import { getElectronAPI } from "@/modules/electron/api";
import { URL } from "url";
import {
  CalendarEvent,
  RecallPermissionKind,
  RecallPermissionStatus,
  ElectronAppInfo,
  RecallSdkState,
  RecallSdkStatePayload,
  ImportAppleNotesResult,
  GetAppleNoteContentResult,
  MenuActionEvent,
  DesktopShortcutEvent,
  DesktopRecallRealtimeEventPayload,
  type RecallMeetingEventPayload,
  DesktopAudioWaveformUpdateEvent,
  StartDesktopAudioUploadArgs,
  StartDesktopAudioUploadResult,
  DesktopAudioRecordingRef,
  DesktopRemoveAudioArtifactArgs,
  DesktopAudioArtifactUploadFailureDebugInfo,
  DesktopAudioArtifactRef,
  DesktopAudioArtifactSnapshot,
  DesktopAudioUploadProgressEvent,
  DesktopAudioUploadCompletedEvent,
  DesktopAudioUploadErrorEvent,
  DesktopRecallReplaceRecordingArgs,
  DesktopRecallStartRecordingArgs,
  DesktopMarketingAttribution,
  DesktopCriticalUpdatePromptResult,
  DesktopCriticalUpdatePromptSource,
  DesktopUpdateRestartBlocker,
  DesktopUpdateStatus,
  DesktopRecordingRecoveryState,
  OAuthAccessTokenGetRequestArgs,
  OAuthAccessTokenGetResponseArgs,
  OAuthAccessTokenSetArgs,
  FloatingAgentConfigureArgs,
  FloatingAgentDragMoveArgs,
  FloatingAgentEvent,
  FloatingAgentFirstMessageOnboardingCompletionArgs,
  FloatingAgentComposerMentionsArgs,
  FloatingAgentForegroundContextArgs,
  FloatingAgentNativeChatLimitUpsellArgs,
  FloatingAgentOpenMemAgentSettingsArgs,
  FloatingAgentOpenMentionArgs,
  FloatingAgentPresentationReleasedArgs,
  FloatingAgentResizeArgs,
  FloatingAgentSetPointerPassthroughArgs,
  FloatingAgentSetInteractionActiveArgs,
  FloatingAgentSetInteractionActiveResult,
  FloatingAgentSetHoldToTalkKeyArgs,
  FloatingAgentSetUnreadStateArgs,
  FloatingAgentTrackProactiveTrialBannerEventArgs,
  FloatingAgentVoiceCaptureFailedArgs,
  FloatingAgentWindowAnchor,
  FloatingAgentWindowDragMoveArgs,
  WorkingContextMatch,
  WorkingContextAddSubscriptionArgs,
  WorkingContextRemoveSubscriptionArgs,
  WorkingContextSnapshot,
  WorkingContextStatus,
  StartRecordingOutcome,
  StartRecordingOutcomeKind,
} from "@/modules/electron/types";
import { ThemeType } from "@/mds/themes/theme-context";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { TrackedEvent } from "@/domains/metrics";

export const electronBindingNavigateToInternalUrl = async ({ url }: { url: URL }) => {
  const electronApi = getElectronAPI();

  await electronApi?.navigateToInternalUrl({ url: url.toString() });

  return null;
};

export const electronBindingNavigateToExternalUrl = async ({ url }: { url: URL }) => {
  const electronApi = getElectronAPI();

  await electronApi?.navigateToExternalUrl({ url: url.toString() });

  return null;
};

export const electronBindingToggleMenu = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.toggleMenu();
};

export const electronBindingMinimizeWindow = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.minimizeWindow();
};

export const electronBindingMaximizeWindow = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.maximizeWindow();
};

export const electronBindingCloseWindow = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.closeWindow();
};

export const electronBindingSetOAuthAccessToken = async ({
  token,
}: OAuthAccessTokenSetArgs): Promise<void> => {
  const electronApi = getElectronAPI();

  await electronApi?.oauthAccessToken?.set({ token });
};

export const electronBindingClearOAuthAccessToken = async (): Promise<void> => {
  const electronApi = getElectronAPI();

  await electronApi?.oauthAccessToken?.clear();
};

export const electronBindingOnOAuthAccessTokenGetRequested = async ({
  callback,
}: {
  callback: (args: OAuthAccessTokenGetRequestArgs) => void;
}) => {
  const electronApi = getElectronAPI();

  return electronApi?.oauthAccessToken?.onGetRequested?.(callback);
};

export const electronBindingRespondToOAuthAccessTokenGetRequest = async ({
  requestId,
  token,
}: OAuthAccessTokenGetResponseArgs): Promise<void> => {
  const electronApi = getElectronAPI();

  await electronApi?.oauthAccessToken?.respondToGetRequest?.({ requestId, token });
};

export const electronBindingOnOAuthAccessTokenRefreshRequested = async ({
  callback,
}: {
  callback: () => void;
}) => {
  const electronApi = getElectronAPI();

  return electronApi?.oauthAccessToken?.onRefreshRequested?.(callback);
};

interface ElectronBindingUpdateNowOptions {
  restartBlockers?: DesktopUpdateRestartBlocker[];
  waitForUpdate?: boolean;
}
export const electronBindingUpdateNow = ({
  restartBlockers = [],
  waitForUpdate = false,
}: ElectronBindingUpdateNowOptions) => {
  const electronApi = getElectronAPI();

  return electronApi?.updateNow({ restartBlockers, waitForUpdate });
};

export const electronBindingGetDesktopUpdateStatus =
  async (): Promise<DesktopUpdateStatus | null> => {
    const electronApi = getElectronAPI();
    return (await electronApi?.desktopUpdater?.getStatus()) ?? null;
  };

export const electronBindingCheckForDesktopUpdate =
  async (): Promise<DesktopUpdateStatus | null> => {
    const electronApi = getElectronAPI();
    return (await electronApi?.desktopUpdater?.check()) ?? null;
  };

export const electronBindingPromptCriticalDesktopUpdate = async ({
  source,
}: {
  source: DesktopCriticalUpdatePromptSource;
}): Promise<DesktopCriticalUpdatePromptResult | null> => {
  const electronApi = getElectronAPI();
  return (await electronApi?.desktopUpdater?.promptCriticalUpdate({ source })) ?? null;
};

export const electronBindingInstallDesktopUpdate = async ({
  isCritical,
  restartBlockers,
}: {
  isCritical: boolean;
  restartBlockers: DesktopUpdateRestartBlocker[];
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.desktopUpdater?.install({ isCritical, restartBlockers });
};

export const electronBindingOnDesktopUpdateStatusChanged = ({
  callback,
}: {
  callback: (status: DesktopUpdateStatus) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.desktopUpdater?.onStatusChanged(callback);
};

export const electronBindingRestartApp = async () => {
  const electronApi = getElectronAPI();

  if (electronApi?.restartApp) {
    await electronApi.restartApp();
  }
};

export const electronBindingOpenSystemSoundSettings = async ({
  focus,
}: {
  focus?: "input" | "output";
} = {}) => {
  const electronApi = getElectronAPI();
  await electronApi?.openSystemSoundSettings?.({ focus });
};

export const electronBindingSetTheme = async ({ theme }: { theme: ThemeType }) => {
  const electronApi = getElectronAPI();
  await electronApi?.setTheme({ theme });
};

export const electronBindingSetCalendarNotificationEnabled = async ({
  enabled,
}: {
  enabled: boolean;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setCalendarNotificationEnabled?.({ enabled });
};

export const electronBindingGetCalendarNotificationEnabled = async (): Promise<{
  enabled: boolean;
}> => {
  const electronApi = getElectronAPI();
  return (await electronApi?.getCalendarNotificationEnabled?.()) ?? { enabled: false };
};

export const electronBindingSetMeetingNotificationEnabled = async ({
  enabled,
}: {
  enabled: boolean;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setMeetingNotificationEnabled?.({ enabled });
};

export const electronBindingGetMeetingNotificationEnabled = async (): Promise<{
  enabled: boolean;
}> => {
  const electronApi = getElectronAPI();
  return (await electronApi?.getMeetingNotificationEnabled?.()) ?? { enabled: false };
};

export const electronBindingSetSpellcheckEnabled = async ({ enabled }: { enabled: boolean }) => {
  const electronApi = getElectronAPI();
  await electronApi?.setSpellcheckEnabled?.({ enabled });
};

export const electronBindingGetSpellcheckEnabled = async (): Promise<{
  enabled: boolean;
}> => {
  const electronApi = getElectronAPI();
  return (await electronApi?.getSpellcheckEnabled?.()) ?? { enabled: true };
};

export const electronBindingSetAdvancedMeetingDetectionShown = async ({
  shown,
}: {
  shown: boolean;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setAdvancedMeetingDetectionShown?.({ shown });
};

export const electronBindingGetAdvancedMeetingDetectionShown = async (): Promise<{
  shown: boolean;
}> => {
  const electronApi = getElectronAPI();
  return (await electronApi?.getAdvancedMeetingDetectionShown?.()) ?? { shown: false };
};

export const electronBindingSetTeamsMeetingDetectionEnabled = async ({
  enabled,
}: {
  enabled: boolean;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setTeamsMeetingDetectionEnabled?.({ enabled });
};

export const electronBindingGetTeamsMeetingDetectionEnabled = async (): Promise<{
  enabled: boolean;
}> => {
  const electronApi = getElectronAPI();
  return (await electronApi?.getTeamsMeetingDetectionEnabled?.()) ?? { enabled: false };
};

type BrowserMetadata = {
  segmentAnonymousId?: string;
  gaClientId?: string;
  gaSessionId?: string;
};

export const electronBindingOnBrowserMetadata = async (
  callback: (metadata: BrowserMetadata) => Promise<void>
): Promise<() => void> => {
  const electronApi = getElectronAPI();

  try {
    if (!electronApi?.onBrowserMetadata) {
      return () => undefined;
    }

    const unsubscribe = await electronApi.onBrowserMetadata(async metadata => {
      await callback({
        segmentAnonymousId: metadata.segmentAnonymousId ?? undefined,
        gaClientId: metadata.gaClientId ?? undefined,
        gaSessionId: metadata.gaSessionId ?? undefined,
      });
    });

    return unsubscribe;
  } catch (error) {
    logger.warn({
      message: "[electronBindingOnBrowserMetadata] Failed to subscribe to browser metadata.",
      info: { error: objectModule.safeErrorAsJson(error as Error) },
    });
  }

  return () => undefined;
};

export const electronBindingGetMarketingAttribution =
  async (): Promise<DesktopMarketingAttribution | null> => {
    const electronApi = getElectronAPI();
    return (await electronApi?.getMarketingAttribution?.()) ?? null;
  };

export const electronBindingOnMarketingAttribution = async (
  callback: (attribution: DesktopMarketingAttribution) => void
): Promise<() => void> => {
  const electronApi = getElectronAPI();

  if (!electronApi?.onMarketingAttribution) {
    return () => undefined;
  }

  return electronApi.onMarketingAttribution(callback);
};

export const electronBindingSetCalendarEvents = async ({
  ongoingEvents,
  upcomingEvents,
}: {
  ongoingEvents: CalendarEvent[];
  upcomingEvents: CalendarEvent[];
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setCalendarEvents?.({ ongoingEvents, upcomingEvents });
};

export const electronBindingOnMenuActionEvent = async ({
  callback,
}: {
  callback: (payload: { actionEvent: MenuActionEvent }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.onMenuActionEvent?.(callback);
};

export const electronBindingOnDesktopShortcutEvent = async ({
  callback,
}: {
  callback: (payload: { shortcutEvent: DesktopShortcutEvent }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.onDesktopShortcutEvent?.(callback);
};

export const electronBindingOnTrackedEvent = async ({
  callback,
}: {
  callback: (payload: { trackedEvent: TrackedEvent }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.onTrackedEvent?.(callback);
};

export const electronBindingSetIsClientReady = async ({ isReady }: { isReady: boolean }) => {
  const electronApi = getElectronAPI();
  await electronApi?.setIsClientReady?.({ isReady });
};

// ==========================
// Recall.ai Desktop SDK Bindings
// ==========================

export const recallBindingPrepareDesktopRecording = async () => {
  const electronApi = getElectronAPI();
  return (await electronApi?.recall.prepareDesktopRecording()) || { windowId: "" };
};

export const recallBindingStartRecording = async (
  args: DesktopRecallStartRecordingArgs
): Promise<StartRecordingOutcome> => {
  const electronApi = getElectronAPI();
  if (!electronApi) {
    logger.error({
      message: "[electronBindingStartRecording] Electron API unavailable",
      info: { audioRecordingId: args.audioRecordingId },
    });
    return {
      outcome: StartRecordingOutcomeKind.Fatal,
      error: "Electron Recall API unavailable",
    };
  }

  return electronApi.recall.startRecording({
    audioRecordingId: args.audioRecordingId,
    windowId: args.windowId,
    uploadToken: args.uploadToken,
    recordingStrategy: args.recordingStrategy,
    calendarEventId: args.calendarEventId,
    startAttemptTrace: args.startAttemptTrace,
    expectedWindowId: args.expectedWindowId,
  });
};

export const recallBindingReplaceRecording = async (
  args: DesktopRecallReplaceRecordingArgs
): Promise<StartRecordingOutcome> => {
  const electronApi = getElectronAPI();
  if (!electronApi) {
    logger.error({
      message: "[electronBindingReplaceRecording] Electron API unavailable",
      info: {
        audioRecordingId: args.audioRecordingId,
        previousAudioRecordingId: args.previousAudioRecordingId,
      },
    });
    return {
      outcome: StartRecordingOutcomeKind.Fatal,
      error: "Electron Recall API unavailable",
    };
  }

  return electronApi.recall.replaceRecording({
    audioRecordingId: args.audioRecordingId,
    previousAudioRecordingId: args.previousAudioRecordingId,
    windowId: args.windowId,
    uploadToken: args.uploadToken,
    recordingStrategy: args.recordingStrategy,
    calendarEventId: args.calendarEventId,
    startAttemptTrace: args.startAttemptTrace,
    expectedWindowId: args.expectedWindowId,
  });
};

export const recallBindingPauseRecording = async (args: { windowId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.pause({ windowId: args.windowId });
};

export const recallBindingResumeRecording = async (args: { windowId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.resume({ windowId: args.windowId });
};

export const recallBindingStopRecording = async (args: { windowId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.stop({ windowId: args.windowId });
};

export const recallBindingGetRecordingSession = async (args: { audioRecordingId: string }) => {
  const electronApi = getElectronAPI();
  return (
    electronApi?.recall.getRecordingSession({ audioRecordingId: args.audioRecordingId }) ?? null
  );
};

export const recallBindingGetRecordingRecoveryState = async (args: {
  audioRecordingId: string;
}): Promise<DesktopRecordingRecoveryState | null> => {
  const electronApi = getElectronAPI();
  return (
    electronApi?.recall.getRecordingRecoveryState({
      audioRecordingId: args.audioRecordingId,
    }) ?? null
  );
};

export const recallBindingPauseRecordingSession = async (args: { audioRecordingId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.pauseRecordingSession({ audioRecordingId: args.audioRecordingId });
};

export const recallBindingResumeRecordingSession = async (args: { audioRecordingId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.resumeRecordingSession({ audioRecordingId: args.audioRecordingId });
};

export const recallBindingStopRecordingSession = async (args: { audioRecordingId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.stopRecordingSession({ audioRecordingId: args.audioRecordingId });
};

export const recallBindingOnEvent = ({
  callback,
}: {
  callback: (payload: DesktopRecallRealtimeEventPayload) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.recall.onEvent?.(callback);
};

export const recallBindingOnMeetingEvent = async ({
  callback,
}: {
  callback: (payload: RecallMeetingEventPayload) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.recall.onMeetingEvent?.(callback);
};

export const recallBindingGetPermissionStatus = async (): Promise<
  Record<RecallPermissionKind, RecallPermissionStatus>
> => {
  const electronApi = getElectronAPI();

  const status = await electronApi?.recall.getPermissionStatus();

  if (!status) {
    return {
      [RecallPermissionKind.Accessibility]: RecallPermissionStatus.NotRequested,
      [RecallPermissionKind.ScreenCapture]: RecallPermissionStatus.NotRequested,
      [RecallPermissionKind.Microphone]: RecallPermissionStatus.NotRequested,
      [RecallPermissionKind.SystemAudio]: RecallPermissionStatus.NotRequested,
      [RecallPermissionKind.FullDiskAccess]: RecallPermissionStatus.NotRequested,
    };
  }

  return status;
};

export const recallBindingRequestPermission = async ({
  permission,
}: {
  permission: RecallPermissionKind;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.requestPermission(permission);
};

export const recallBindingOpenPermissionPane = async ({
  permission,
}: {
  permission: RecallPermissionKind;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.openPermissionPane(permission);
};

export const recallBindingOnPermissionStatus = async ({
  callback,
}: {
  callback: (payload: {
    event: { permission: RecallPermissionKind; status: RecallPermissionStatus };
  }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.recall.onPermissionStatus?.(callback);
};

export const recallBindingGetSdkState = async () => {
  const electronApi = getElectronAPI();
  const payload = await electronApi?.recall.getSdkState();

  return (
    payload ?? {
      state: RecallSdkState.Idle,
    }
  );
};

export const recallBindingOnSdkState = async ({
  callback,
}: {
  callback: (payload: RecallSdkStatePayload) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.recall.onSdkState?.(callback);
};

export const recallBindingTestUnexpectedShutdown = async () => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.testUnexpectedShutdown?.();
};

export const electronBindingResetTccAndClearCache = async ({ appName }: { appName: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.resetTccAndClearCache?.({ appName });
};

export const electronBindingSetClientMetadata = async ({
  context,
}: {
  context: Record<string, unknown>;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setClientMetadata?.({ context });
};

export const electronBindingClearClientMetadata = async () => {
  const electronApi = getElectronAPI();
  await electronApi?.clearClientMetadata?.();
};

export const electronBindingGetElectronAppInfo = async (): Promise<ElectronAppInfo | null> => {
  const electronApi = getElectronAPI();
  const info = await electronApi?.getElectronAppInfo?.();
  return info ?? null;
};

export const electronBindingConfigureFloatingAgent = async (
  args: FloatingAgentConfigureArgs
): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.configure?.(args);
};

export const electronBindingSetFloatingAgentUnreadState = async (
  args: FloatingAgentSetUnreadStateArgs
): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.setUnreadState?.(args);
};

export const electronBindingSetFloatingAgentInteractionActive = async (
  args: FloatingAgentSetInteractionActiveArgs
): Promise<FloatingAgentSetInteractionActiveResult> => {
  const electronApi = getElectronAPI();
  return (await electronApi?.floatingAgent?.setInteractionActive?.(args)) ?? null;
};

export const electronBindingFloatingAgentPresentationReleased = async (
  args: FloatingAgentPresentationReleasedArgs
): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.presentationReleased?.(args);
};

export const electronBindingSetFloatingAgentHoldToTalkKey = async (
  args: FloatingAgentSetHoldToTalkKeyArgs
): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.setHoldToTalkKey?.(args);
};

export const electronBindingStartFloatingAgentOnboardingNudge = async (): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.startOnboardingNudge?.();
};

export const electronBindingCompleteFloatingAgentFirstMessageOnboarding = async (
  args: FloatingAgentFirstMessageOnboardingCompletionArgs
): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.completeFirstMessageOnboarding?.(args);
};

export const electronBindingFloatingAgentDragMove = (args: FloatingAgentDragMoveArgs): void => {
  const electronApi = getElectronAPI();
  electronApi?.floatingAgent?.dragMove?.(args);
};

export const electronBindingFloatingAgentDragEnd = async (
  args: FloatingAgentDragMoveArgs
): Promise<{ anchor: FloatingAgentWindowAnchor } | null> => {
  const electronApi = getElectronAPI();
  return (await electronApi?.floatingAgent?.dragEnd?.(args)) ?? null;
};

export const electronBindingFloatingAgentWindowDragMove = (
  args: FloatingAgentWindowDragMoveArgs
): void => {
  const electronApi = getElectronAPI();
  electronApi?.floatingAgent?.windowDragMove?.(args);
};

export const electronBindingFloatingAgentWindowDragEnd = async (
  args: FloatingAgentWindowDragMoveArgs
): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.windowDragEnd?.(args);
};

export const electronBindingFloatingAgentResize = async (
  args: FloatingAgentResizeArgs
): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.resize?.(args);
};

export const electronBindingFloatingAgentSetPointerPassthrough = (
  args: FloatingAgentSetPointerPassthroughArgs
): void => {
  const electronApi = getElectronAPI();
  electronApi?.floatingAgent?.setPointerPassthrough?.(args);
};

export const electronBindingFloatingAgentVoiceCaptureFailed = (
  args: FloatingAgentVoiceCaptureFailedArgs
): void => {
  const electronApi = getElectronAPI();
  electronApi?.floatingAgent?.voiceCaptureFailed?.(args);
};

export const electronBindingFloatingAgentGetAnchor =
  async (): Promise<FloatingAgentWindowAnchor | null> => {
    const electronApi = getElectronAPI();
    return (await electronApi?.floatingAgent?.getAnchor?.()) ?? null;
  };

export const electronBindingFloatingAgentSummonMainApp = async (): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.summonMainApp?.();
};

export const electronBindingFloatingAgentOpenPanel = async (): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.openPanel?.();
};

export const electronBindingFloatingAgentOpenNativeChatLimitUpsell = async (
  args: FloatingAgentNativeChatLimitUpsellArgs
): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.openNativeChatLimitUpsell?.(args);
};

export const electronBindingFloatingAgentOpenProactiveUpsell = async (): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.openProactiveUpsell?.();
};

export const electronBindingFloatingAgentHide = async (): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.hide?.();
};

export const electronBindingFloatingAgentTooltipShow = async (): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.tooltipShow?.();
};

export const electronBindingFloatingAgentTooltipHide = async (): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.tooltipHide?.();
};

export const electronBindingFloatingAgentSetForegroundContext = async (
  args: FloatingAgentForegroundContextArgs
): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.setForegroundContext?.(args);
};

export const electronBindingFloatingAgentGetForegroundContext =
  async (): Promise<FloatingAgentForegroundContextArgs> => {
    const electronApi = getElectronAPI();
    return (await electronApi?.floatingAgent?.getForegroundContext?.()) ?? { notes: [] };
  };

export const electronBindingFloatingAgentAttachComposerMentions = async (
  args: FloatingAgentComposerMentionsArgs
): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.attachComposerMentions?.(args);
};

export const electronBindingFloatingAgentTakeComposerMentions =
  async (): Promise<FloatingAgentComposerMentionsArgs | null> => {
    const electronApi = getElectronAPI();
    return (await electronApi?.floatingAgent?.takeComposerMentions?.()) ?? null;
  };

export const electronBindingFloatingAgentTrackProactiveTrialBannerEvent = async (
  args: FloatingAgentTrackProactiveTrialBannerEventArgs
): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.trackProactiveTrialBannerEvent?.(args);
};

export const electronBindingFloatingAgentRendererReady = (): void => {
  const electronApi = getElectronAPI();
  electronApi?.floatingAgent?.rendererReady?.();
};

export const electronBindingFloatingAgentOpenMention = async (
  args: FloatingAgentOpenMentionArgs
): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.openMention?.(args);
};

export const electronBindingFloatingAgentOpenTasks = async (): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.openTasks?.();
};

export const electronBindingFloatingAgentOpenMemAgentSettings = async (
  args: FloatingAgentOpenMemAgentSettingsArgs
): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.floatingAgent?.openMemAgentSettings?.(args);
};

export const electronBindingFloatingAgentOnOpenMention = (
  cb: (args: FloatingAgentOpenMentionArgs) => void
): (() => void) => {
  const electronApi = getElectronAPI();
  return electronApi?.floatingAgent?.onOpenMention?.(cb) ?? (() => undefined);
};

export const electronBindingFloatingAgentOnOpenTasks = (cb: () => void): (() => void) => {
  const electronApi = getElectronAPI();
  return electronApi?.floatingAgent?.onOpenTasks?.(cb) ?? (() => undefined);
};

export const electronBindingFloatingAgentOnOpenMemAgentSettings = (
  cb: (args: FloatingAgentOpenMemAgentSettingsArgs) => void
): (() => void) => {
  const electronApi = getElectronAPI();
  return electronApi?.floatingAgent?.onOpenMemAgentSettings?.(cb) ?? (() => undefined);
};

export const electronBindingFloatingAgentOnEvent = (
  cb: (event: FloatingAgentEvent) => void
): (() => void) => {
  const electronApi = getElectronAPI();
  return electronApi?.floatingAgent?.onEvent?.(cb) ?? (() => undefined);
};

export const electronBindingGetAudioArtifactRef = async (
  args: DesktopAudioRecordingRef
): Promise<DesktopAudioArtifactRef | null> => {
  const electronApi = getElectronAPI();

  return (await electronApi?.audioArtifacts.getAudioArtifactRef(args)) ?? null;
};

export const electronBindingGetAudioArtifactSnapshot = async (
  args: DesktopAudioRecordingRef
): Promise<DesktopAudioArtifactSnapshot | null> => {
  const electronApi = getElectronAPI();

  return (await electronApi?.audioArtifacts.getAudioArtifactSnapshot(args)) ?? null;
};

export const electronBindingGetAudioArtifactUploadFailureDebugInfo = async (
  args: DesktopAudioRecordingRef
): Promise<DesktopAudioArtifactUploadFailureDebugInfo | null> => {
  const electronApi = getElectronAPI();

  return (await electronApi?.audioArtifacts.getAudioArtifactUploadFailureDebugInfo(args)) ?? null;
};

export const electronBindingFinalizeAudioArtifact = async (
  args: DesktopAudioRecordingRef
): Promise<DesktopAudioArtifactRef | null> => {
  const electronApi = getElectronAPI();

  return (await electronApi?.audioArtifacts.finalizeAudioArtifact(args)) ?? null;
};

export const electronBindingRemoveAudioArtifact = async (
  args: DesktopRemoveAudioArtifactArgs
): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.audioArtifacts.removeAudioArtifact(args);
};

export const electronBindingStartAudioUpload = async (
  args: StartDesktopAudioUploadArgs
): Promise<StartDesktopAudioUploadResult> => {
  const electronApi = getElectronAPI();

  return (
    (await electronApi?.audioUpload.startAudioUpload(args)) ?? {
      success: false,
      error: "Desktop audio upload is unavailable",
    }
  );
};

export const electronBindingOnAudioUploadProgress = ({
  callback,
}: {
  callback: (payload: DesktopAudioUploadProgressEvent) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.audioUpload.onProgress?.(callback);
};

export const electronBindingOnAudioUploadCompleted = ({
  callback,
}: {
  callback: (payload: DesktopAudioUploadCompletedEvent) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.audioUpload.onCompleted?.(callback);
};

export const electronBindingOnAudioUploadError = ({
  callback,
}: {
  callback: (payload: DesktopAudioUploadErrorEvent) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.audioUpload.onError?.(callback);
};

export const electronBindingOnDesktopAudioWaveformUpdate = ({
  callback,
}: {
  callback: (payload: DesktopAudioWaveformUpdateEvent) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.audioWaveform.onUpdate?.(callback);
};

export const electronBindingGetWorkingContextStatus = async (): Promise<WorkingContextStatus> => {
  const electronApi = getElectronAPI();

  return (
    (await electronApi?.workingContext?.getStatus()) ?? {
      running: false,
      activeSubscriptionCount: 0,
      recentObservationCount: 0,
      recentMatchCount: 0,
      observationCapacity: 0,
      matchCapacity: 0,
    }
  );
};

export const electronBindingGetWorkingContextSnapshot =
  async (): Promise<WorkingContextSnapshot | null> => {
    const electronApi = getElectronAPI();

    return (await electronApi?.workingContext?.getSnapshot()) ?? null;
  };

export const electronBindingAddWorkingContextSubscription = async (
  args: WorkingContextAddSubscriptionArgs
): Promise<WorkingContextSnapshot | null> => {
  const electronApi = getElectronAPI();

  return (await electronApi?.workingContext?.addSubscription(args)) ?? null;
};

export const electronBindingRemoveWorkingContextSubscription = async (
  args: WorkingContextRemoveSubscriptionArgs
): Promise<WorkingContextSnapshot | null> => {
  const electronApi = getElectronAPI();

  return (await electronApi?.workingContext?.removeSubscription(args)) ?? null;
};

export const electronBindingOnWorkingContextMatch = ({
  callback,
}: {
  callback: (payload: WorkingContextMatch) => void;
}) => {
  const electronApi = getElectronAPI();

  return electronApi?.workingContext?.onMatch?.(callback);
};

export const electronBindingImportAppleNotes = async (options?: {
  collectAttachments?: boolean;
}): Promise<ImportAppleNotesResult> => {
  const electronApi = getElectronAPI();
  return (
    (await electronApi?.importAppleNotes?.(options)) ?? {
      success: false,
      error: "Not available",
      notes: [],
      notesPerTag: {},
    }
  );
};

export const electronBindingGetAppleNoteContent = async (
  noteId: number
): Promise<GetAppleNoteContentResult> => {
  const electronApi = getElectronAPI();
  const result = await electronApi?.getAppleNoteContent?.(noteId);
  if (!result) {
    throw new Error("getAppleNoteContent not available");
  }
  return result;
};

export const electronBindingCloseAppleNotesReader = async (): Promise<void> => {
  const electronApi = getElectronAPI();
  await electronApi?.closeAppleNotesReader?.();
};
